import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { API_URL } from '../../config/constants';
import { useAuth } from '../../context/AuthContext';

interface Book {
  id: number;
  title: string;
  author: string;
  description: string;
  category: string;
  tags: string[] | string;
  publishedYear: number;
  fileName: string;
  fileType: string;
  coverImage: string | null;
}

const ManageBooks: React.FC = () => {
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('');
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [selectedBooks, setSelectedBooks] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  
  const { token } = useAuth();
  const navigate = useNavigate();
  
  // Încarcă lista de cărți
  useEffect(() => {
    const fetchBooks = async () => {
      try {
        setLoading(true);
        
        const response = await fetch(`${API_URL}/books`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Eroare la încărcarea cărților');
        }
        
        const data = await response.json();
        setBooks(Array.isArray(data) ? data : data.books || []);
      } catch (error) {
        console.error('Eroare:', error);
        setError('Eroare la încărcarea cărților');
      } finally {
        setLoading(false);
      }
    };
    
    fetchBooks();
  }, [token]);
  
  // Gestionează căutarea
  const filteredBooks = books.filter(book => {
    const query = searchQuery.toLowerCase();
    return (
      book.title.toLowerCase().includes(query) || 
      book.author.toLowerCase().includes(query) ||
      book.category.toLowerCase().includes(query) ||
      (book.description && book.description.toLowerCase().includes(query))
    );
  });
  
  // Gestionează selectarea/deselectarea tuturor cărților
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedBooks([]);
    } else {
      setSelectedBooks(filteredBooks.map(book => book.id));
    }
    setSelectAll(!selectAll);
  };

  // Gestionează selectarea/deselectarea unei singure cărți
  const handleSelectBook = (bookId: number) => {
    if (selectedBooks.includes(bookId)) {
      setSelectedBooks(selectedBooks.filter(id => id !== bookId));
      setSelectAll(false);
    } else {
      setSelectedBooks([...selectedBooks, bookId]);
      if (selectedBooks.length + 1 === filteredBooks.length) {
        setSelectAll(true);
      }
    }
  };

  // Gestionează ștergerea unei cărți individuale
  const handleDelete = async (bookId: number) => {
    if (!window.confirm('Sigur doriți să ștergeți această carte? Această acțiune este ireversibilă.')) {
      return;
    }
    
    try {
      setMessage('Se șterge cartea...');
      setMessageType('success');
      
      const response = await fetch(`${API_URL}/books/${bookId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Eroare la ștergerea cărții: ${response.status}`);
      }
      
      // Actualizează lista de cărți
      setBooks(books.filter(book => book.id !== bookId));
      
      setMessage('Cartea a fost ștearsă cu succes');
      setMessageType('success');
      
      // Resetează mesajul după 3 secunde
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000);
    } catch (error: any) {
      console.error('Eroare:', error);
      setMessage(`Eroare la ștergerea cărții: ${error.message || 'Verificați consola pentru detalii'}`);
      setMessageType('error');
      
      // Resetează mesajul de eroare după 5 secunde
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 5000);
    }
  };

  // Gestionează ștergerea în bulk a cărților selectate
  const handleBulkDelete = async () => {
    if (selectedBooks.length === 0) {
      setMessage('Nicio carte selectată pentru ștergere');
      setMessageType('error');
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000);
      return;
    }

    if (!window.confirm(`Sigur doriți să ștergeți ${selectedBooks.length} cărți? Această acțiune este ireversibilă.`)) {
      return;
    }

    setMessage(`Se șterg ${selectedBooks.length} cărți...`);
    setMessageType('success');

    let successCount = 0;
    let errorCount = 0;

    // Procesăm ștergerea cărților una câte una
    for (const bookId of selectedBooks) {
      try {
        const response = await fetch(`${API_URL}/books/${bookId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        console.error(`Eroare la ștergerea cărții #${bookId}:`, error);
        errorCount++;
      }
    }

    // Actualizăm lista de cărți după ștergeri
    if (successCount > 0) {
      setBooks(books.filter(book => !selectedBooks.includes(book.id)));
      setSelectedBooks([]);
      setSelectAll(false);
    }

    // Afișăm un mesaj corespunzător
    if (errorCount === 0) {
      setMessage(`${successCount} cărți au fost șterse cu succes`);
      setMessageType('success');
    } else if (successCount === 0) {
      setMessage(`Eroare la ștergerea cărților. Nicio carte nu a fost ștearsă.`);
      setMessageType('error');
    } else {
      setMessage(`${successCount} cărți șterse cu succes, ${errorCount} cărți nu au putut fi șterse`);
      setMessageType('success');
    }

    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };
  
  // Gestionează schimbarea fișierului de copertă
  const handleCoverFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setCoverFile(e.target.files[0]);
    }
  };
  
  // Gestionează încărcarea imaginii de copertă
  const handleUploadCover = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedBook) {
      return;
    }
    
    if (!coverFile) {
      setMessage('Selectați un fișier pentru copertă');
      setMessageType('error');
      return;
    }
    
    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('coverImage', coverFile);
      
      const response = await fetch(`${API_URL}/books/${selectedBook.id}/cover`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Eroare la încărcarea copertei');
      }
      
      const data = await response.json();
      
      // Actualizează cartea în lista locală
      setBooks(books.map(book => {
        if (book.id === selectedBook.id) {
          return { ...book, coverImage: data.coverImage };
        }
        return book;
      }));
      
      // Resetează starea
      setSelectedBook(null);
      setCoverFile(null);
      setMessage('Coperta a fost adăugată cu succes');
      setMessageType('success');
      
      // Resetează mesajul după 3 secunde
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000);
    } catch (error) {
      console.error('Eroare:', error);
      setMessage('Eroare la încărcarea copertei');
      setMessageType('error');
    } finally {
      setUploading(false);
    }
  };
  
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      {/* Spațiu pentru header-ul fix */}
      <div className="pt-24 md:pt-28"></div>
      
      <main className="flex-grow bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Gestionare Cărți</h1>
              <p className="text-gray-600">Administrează cărțile din biblioteca digitală</p>
            </div>
            <div className="mt-4 md:mt-0">
              <Link
                to="/admin/books/add"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Adaugă Carte Nouă
              </Link>
            </div>
          </div>
          
          {/* Mesaj de succes/eroare */}
          {message && (
            <div className={`p-4 mb-6 rounded-md ${messageType === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
              {message}
            </div>
          )}
          
          {/* Modal pentru încărcarea copertei */}
          {selectedBook && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Încarcă Coperta</h2>
                <p className="text-gray-600 mb-4">Adaugă o copertă pentru <span className="font-medium">{selectedBook.title}</span></p>
                
                <form onSubmit={handleUploadCover}>
                  <div className="mb-4">
                    <label htmlFor="coverFile" className="block text-sm font-medium text-gray-700 mb-1">
                      Fișier Copertă (JPG, PNG, GIF)
                    </label>
                    <input
                      type="file"
                      id="coverFile"
                      accept="image/*"
                      onChange={handleCoverFileChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  
                  {/* Previzualizare copertă actuală */}
                  {selectedBook.coverImage && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-1">Coperta actuală</p>
                      <img 
                        src={`${API_URL}${selectedBook.coverImage}`} 
                        alt={`Coperta cărții ${selectedBook.title}`}
                        className="h-32 object-contain border border-gray-200 rounded"
                      />
                    </div>
                  )}
                  
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setSelectedBook(null)}
                      className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Anulează
                    </button>
                    <button
                      type="submit"
                      disabled={uploading}
                      className={`px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${uploading ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {uploading ? 'Se încarcă...' : 'Încarcă'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
          
          {/* Bară de căutare */}
          <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
            <div className="flex">
              <input
                type="text"
                placeholder="Caută cărți după titlu, autor sau categorie"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-grow px-4 py-2 border border-gray-300 rounded-l-md focus:ring-blue-500 focus:border-blue-500"
              />
              <button className="bg-blue-600 text-white px-4 py-2 rounded-r-md hover:bg-blue-700">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>
          </div>
          
          {/* Acțiuni în bulk */}
          <div className="bg-white p-4 rounded-lg shadow-sm mb-6 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {selectedBooks.length === 0 ? (
                'Selectați cărți pentru acțiuni în bulk'
              ) : (
                <span>
                  <span className="font-medium">{selectedBooks.length}</span> cărți selectate
                </span>
              )}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleBulkDelete}
                disabled={selectedBooks.length === 0}
                className={`flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${selectedBooks.length === 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Șterge cărțile selectate
              </button>
              {selectedBooks.length > 0 && (
                <button
                  onClick={() => {
                    setSelectedBooks([]);
                    setSelectAll(false);
                  }}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Anulează selecția
                </button>
              )}
            </div>
          </div>
          
          {/* Lista de cărți */}
          {loading ? (
            <div className="bg-white p-8 rounded-lg shadow text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Se încarcă cărțile...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 p-8 rounded-lg shadow text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-red-500 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="mt-4 text-red-800">{error}</p>
            </div>
          ) : filteredBooks.length === 0 ? (
            <div className="bg-white p-8 rounded-lg shadow text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="mt-4 text-gray-600">
                {searchQuery ? 'Nu s-au găsit cărți care să corespundă căutării tale.' : 'Nu există încă nicio carte în bibliotecă.'}
              </p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-4 px-4 py-2 text-blue-600 hover:text-blue-800"
                >
                  Resetează căutarea
                </button>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-3 py-3 text-left">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                        </div>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Titlu & Autor
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Categorie
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Copertă
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        An
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Format
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acțiuni
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredBooks.map((book) => (
                      <tr key={book.id} className={`hover:bg-gray-50 ${selectedBooks.includes(book.id) ? 'bg-blue-50' : ''}`}>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={selectedBooks.includes(book.id)}
                              onChange={() => handleSelectBook(book.id)}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{book.title}</div>
                              <div className="text-sm text-gray-500">{book.author}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                            {book.category || 'Necategorizat'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {book.coverImage ? (
                            <div className="flex items-center">
                              <img 
                                src={`${API_URL}${book.coverImage}`} 
                                alt={`Coperta cărții ${book.title}`}
                                className="h-12 w-10 object-cover rounded"
                              />
                              <button
                                onClick={() => setSelectedBook(book)}
                                className="ml-2 text-xs text-blue-600 hover:text-blue-800"
                              >
                                Schimbă
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setSelectedBook(book)}
                              className="px-2 py-1 text-xs text-white bg-blue-600 rounded hover:bg-blue-700"
                            >
                              Adaugă
                            </button>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {book.publishedYear || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {book.fileType ? (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              {book.fileType.toUpperCase()}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-500">N/A</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <Link
                              to={`/admin/books/edit/${book.id}`}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              Editează
                            </Link>
                            <button
                              onClick={() => handleDelete(book.id)}
                              className="text-red-600 hover:text-red-900 flex items-center"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Șterge
                            </button>
                            <Link
                              to={`/books/${book.id}`}
                              className="text-green-600 hover:text-green-900"
                              target="_blank"
                            >
                              Vizualizează
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ManageBooks; 